/**
 * Reunification domain types based on ADR-052 and ADR-064.
 *
 * Covers the core models a ONE (parent) user interacts with:
 * students, guardians, events, sites, and release logs.
 *
 * The status enums are derived from the generated OpenAPI
 * schema (`@tiaki/http-client/types`) rather than hand-rolled, so the
 * values match what the backend actually emits (lowercase snake_case
 * per `server/apps/reunification/constants.py`). Hand-rolled UPPERCASE
 * variants drifted from real payloads and silently broke badge, timeline,
 * and gating comparisons.
 */

import type { components } from '@tiaki/http-client/types';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Four-phase alert lifecycle (ADR-052) */
export type AlertPhase = 'PREPARE' | 'PREVENT' | 'RESPOND' | 'RECOVER';

/** Top-level reunification event status (generated schema, backend casing) */
export type ReunificationStatus =
  components['schemas']['ReunificationEventStatus'];

/** Student status flow from ADR-064 (generated schema, backend casing) */
export type StudentStatus = components['schemas']['ReunificationStudentStatus'];

/** Guardian status flow from ADR-064 (generated schema, backend casing) */
export type GuardianStatus =
  components['schemas']['ReunificationGuardianStatus'];

/** Staff roles from ADR-052 */
export type StaffRole =
  | 'RUNNER'
  | 'RELEASER'
  | 'RECORDER'
  | 'REUNIFIER'
  | 'GREETER'
  | 'SUPERVISOR'
  | 'MEDICAL'
  | 'OTHER';

/** Station types from ADR-064 */
export type StationType =
  | 'GREETER'
  | 'CHECK_IN'
  | 'WAITING'
  | 'ASSEMBLY'
  | 'LEO_INTERVIEW'
  | 'CRISIS_COUNSELING'
  | 'REUNIFICATION_ROOM'
  | 'EXIT'
  | 'MEDICAL'
  | 'COMMAND_POST'
  | 'MEDIA_STAGING'
  | 'BUS_STAGING'
  | 'OTHER';

/** Physical site types from ADR-064 */
export type SiteType =
  | 'SCHOOL'
  | 'COMMUNITY_CENTER'
  | 'CHURCH'
  | 'STADIUM'
  | 'PARK'
  | 'PARKING_LOT'
  | 'CONVENTION_CENTER';

// ---------------------------------------------------------------------------
// Domain models
// ---------------------------------------------------------------------------

export interface ReunificationEvent {
  id: string;
  name: string;
  status: ReunificationStatus;
  alertPhase: AlertPhase;
  activatedAt: string; // ISO datetime
  completedAt?: string;
  siteName: string;
  siteType: SiteType;
  siteAddress: string;
  studentCount: number;
  reunifiedCount: number;
  guardianCount: number;
}

export interface Student {
  id: string;
  firstName: string;
  lastName: string;
  grade: string;
  school: string;
  photoUrl?: string;
  status: StudentStatus;
  eventId?: string;
  lastUpdated: string;
}

export interface Guardian {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  photoUrl?: string;
  status: GuardianStatus;
  idVerified: boolean;
  students: string[]; // student IDs
}

export interface AuthorizedPickup {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
  idVerified: boolean;
  studentIds: string[];
}

export interface ReleaseLog {
  id: string;
  eventId: string;
  studentId: string;
  guardianId: string;
  releasedAt: string;
  releasedBy: string; // staff name
  verificationMethod: 'ID_CHECK' | 'PRE_VERIFIED' | 'KNOWN_PERSON';
  signature?: string;
  notes?: string;
}

export interface Notification {
  id: string;
  type: 'ALERT' | 'REUNIFICATION' | 'STATUS_UPDATE' | 'INFO';
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  eventId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Human-readable label for a student status.
 *
 * `Record<StudentStatus, string>` is deliberately exhaustive over the
 * generated enum: a backend status added to the schema becomes a tsc
 * error here rather than an `undefined` label at runtime.
 */
export const STUDENT_STATUS_LABELS: Record<StudentStatus, string> = {
  secured_at_incident: 'Secured at Incident',
  transported: 'In Transport',
  arrived_at_site: 'Arrived at Site',
  in_assembly: 'In Assembly',
  in_leo_interview: 'LEO Interview',
  in_crisis_counseling: 'Crisis Counseling',
  cleared_for_release: 'Cleared for Release',
  in_reunification_room: 'In Reunification Room',
  reunified: 'Reunified',
  exited: 'Exited',
  reported_safe_offsite: 'Reported Safe Offsite',
  reunified_offsite: 'Reunified Offsite',
  safe_at_home: 'Safe at Home',
  at_hospital: 'At Hospital',
  released_to_third_party: 'Released to Third Party',
  self_transported: 'Self-Transported',
  // Special statuses
  absent: 'Absent',
  missing: 'Missing',
  medical: 'Medical Care',
  // Legacy statuses (ADR-052, still emitted by the backend)
  unaccounted: 'Unaccounted',
  accounted: 'Accounted',
  with_staff: 'With Staff',
  released: 'Released',
};

/** Color mapping for student statuses */
export const STUDENT_STATUS_COLORS: Record<StudentStatus, string> = {
  secured_at_incident: 'orange.500',
  transported: 'blue.400',
  arrived_at_site: 'blue.500',
  in_assembly: 'blue.600',
  in_leo_interview: 'purple.500',
  in_crisis_counseling: 'purple.400',
  cleared_for_release: 'green.400',
  in_reunification_room: 'green.500',
  reunified: 'green.600',
  exited: 'neutral.500',
  reported_safe_offsite: 'green.300',
  reunified_offsite: 'green.500',
  safe_at_home: 'green.600',
  at_hospital: 'red.500',
  released_to_third_party: 'yellow.500',
  self_transported: 'yellow.600',
  // Special statuses
  absent: 'neutral.400',
  missing: 'red.600',
  medical: 'red.500',
  // Legacy statuses
  unaccounted: 'orange.500',
  accounted: 'teal.500',
  with_staff: 'blue.500',
  released: 'green.600',
};

/** Human-readable label for a guardian status */
export const GUARDIAN_STATUS_LABELS: Record<GuardianStatus, string> = {
  notified: 'Notified',
  pre_verified: 'Pre-Verified',
  en_route: 'En Route',
  arrived: 'Arrived',
  checked_in: 'Checked In',
  in_waiting_area: 'In Waiting Area',
  matched_to_student: 'Matched to Student',
  reunified: 'Reunified',
  exited: 'Exited',
  denied: 'Denied',
  // Legacy statuses (ADR-052, still emitted by the backend)
  not_arrived: 'Not Arrived',
  verified: 'Verified',
  released: 'Released',
};

export const GUARDIAN_STATUS_COLORS: Record<GuardianStatus, string> = {
  notified: 'blue.400',
  pre_verified: 'teal.400',
  en_route: 'blue.500',
  arrived: 'blue.600',
  checked_in: 'teal.500',
  in_waiting_area: 'orange.500',
  matched_to_student: 'green.400',
  reunified: 'green.600',
  exited: 'neutral.500',
  denied: 'red.500',
  // Legacy statuses
  not_arrived: 'neutral.400',
  verified: 'teal.500',
  released: 'green.600',
};
